import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Trash2, Globe, X, Check, Loader2,
  ShieldCheck, ChevronLeft, ChevronRight, ExternalLink,
  ArrowRight, ArrowLeft, X as XIcon,
} from "lucide-react";
import { FORMAT_VERIFICATION_OPTIONS, VERDICT_OPTIONS, STATUT_MAP, langToApi, generateSlug } from "@/lib/enums";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { useI18n } from "@/lib/i18n";
import { ModulePanel, EditorialStatusBadge } from "@/components/admin/AdminPrimitives";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import {
  useVerifications,
  useCreateVerification,
  useDeleteVerification,
  useMesVerifications,
  useValiderVerification,
  useRefuserVerification,
} from "@/hooks/useVerifications";
import { useCategories } from "@/hooks/useCategories";
import { hasAnyRole, useBloquerPartenaire } from "@/pages/admin/RequireAdmin";
import { VerificationDto } from "@/types/verification.types";

const LANGUES = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "bm", label: "Bambara" },
  { code: "ha", label: "Haoussa" },
  { code: "mos", label: "Mooré" },
];

const VERDICTS = VERDICT_OPTIONS;

function VerdictBadge({ verdict }: { verdict: string }) {
  const v = VERDICTS.find((vd) => vd.value === verdict);
  if (!v) return <span className="text-xs text-muted-foreground">{verdict}</span>;
  return (
    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${v.color}`}>
      {v.label}
    </span>
  );
}

// ─── Modal de création (2 étapes, calquée sur ArticleModal) ──────────────────
type Step = "meta" | "traduction";

function VerificationModal({
  onClose,
  categories,
  onSaved,
}: {
  onClose: () => void;
  categories: any[];
  onSaved: () => void;
}) {
  const { t, locale } = useI18n();
  const createVerification = useCreateVerification();
  const [step, setStep] = useState<Step>("meta");

  // — Étape 1 : métadonnées
  const [verdict, setVerdict] = useState("NON_PROUVE");
  const [format, setFormat] = useState("TEXTE");
  const [categorieId, setCategorieId] = useState<number>(0);

  // — Étape 2 : traduction principale FR (requise) + autres langues
  const [langue, setLangue] = useState("fr");
  const [traductions, setTraductions] = useState<Record<string, any>>({
    fr: {
      titre: "",
      affirmation: "",
      explicationCourte: "",
      ceQuiCircule: "",
      ceQueNousAvonsVerifie: "",
      preuves: "",
      pourquoiImportant: "",
      partageSecurise: "",
    },
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const categorieSelectionnee = categories.find((c: any) => c.id === categorieId);

  const setTradField = (lang: string, field: string, value: string) => {
    setTraductions((prev) => ({
      ...prev,
      [lang]: { ...(prev[lang] || {}), [field]: value },
    }));
  };

  const activeLang = (lang: string) => {
    if (!traductions[lang]) {
      setTraductions((prev) => ({
        ...prev,
        [lang]: {
          titre: "",
          affirmation: "",
          explicationCourte: "",
          ceQuiCircule: "",
          ceQueNousAvonsVerifie: "",
          preuves: "",
          pourquoiImportant: "",
          partageSecurise: "",
        },
      }));
    }
    setLangue(lang);
  };

  const goToStep2 = () => {
    setError("");
    setStep("traduction");
  };

  // Slug dérivé de l'affirmation FR + slug de la catégorie (comme les articles)
  const affirmationFr = traductions.fr?.affirmation || "";
  const slugPreview =
    affirmationFr
      ? generateSlug(affirmationFr, categorieSelectionnee?.slug || "")
      : null;

  const handleSave = async () => {
    if (!affirmationFr.trim()) {
      setError(t("admin.factcheck.affirmationRequired"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const slug = generateSlug(affirmationFr, categorieSelectionnee?.slug || "");
      const payload = {
        slug,
        date: new Date().toISOString(),
        verdict,
        format,
        categorieId: categorieId || undefined,
        traductions: Object.entries(traductions)
          .filter(([, trad]) => trad?.affirmation?.trim())
          .map(([lang, trad]) => ({ langue: langToApi(lang), ...trad })),
      };
      await createVerification.mutateAsync(payload);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t("admin.factcheck.creationError"));
    } finally {
      setSaving(false);
    }
  };

  const TRAD_FIELDS = [
    { key: "titre", label: "Titre", rows: 1 },
    { key: "affirmation", label: t("admin.factcheck.field.affirmation"), rows: 2, required: true },
    { key: "explicationCourte", label: t("admin.factcheck.field.explication"), rows: 2 },
    { key: "ceQuiCircule", label: t("admin.factcheck.field.ceQuiCircule"), rows: 3 },
    { key: "ceQueNousAvonsVerifie", label: t("admin.factcheck.field.ceQueNousAvonsVerifie"), rows: 3 },
    { key: "preuves", label: t("admin.factcheck.field.preuves"), rows: 3 },
    { key: "pourquoiImportant", label: t("admin.factcheck.field.pourquoiImportant"), rows: 2 },
    { key: "partageSecurise", label: t("admin.factcheck.field.partageSecurise"), rows: 2 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl rounded-lg border border-border bg-background shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="font-serif text-lg font-semibold text-foreground">{t("admin.factcheck.new")}</h2>
            <div className="flex items-center gap-2 mt-1">
              {(["meta", "traduction"] as Step[]).map((s, i) => (
                <div key={s} className="flex items-center gap-1.5">
                  <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                    step === s
                      ? "bg-foreground text-background"
                      : i === 0 && step === "traduction"
                        ? "bg-verified text-white"
                        : "bg-surface text-muted-foreground"
                  }`}>
                    {i === 0 && step === "traduction" ? <Check className="h-3 w-3" /> : i + 1}
                  </div>
                  <span className={`text-[10px] font-semibold ${step === s ? "text-foreground" : "text-muted-foreground"}`}>
                    {s === "meta" ? t("admin.factcheck.step.meta") : t("admin.factcheck.step.translation")}
                  </span>
                  {i === 0 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                </div>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-surface text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="rounded-md bg-alert/10 border border-alert/30 px-4 py-2 text-xs text-alert font-medium">{error}</div>
          )}

          {/* ── Étape 1 : verdict, format, rubrique ── */}
          {step === "meta" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.factcheck.verdict")}</label>
                <div className="flex flex-wrap gap-2">
                  {VERDICTS.map((v) => (
                    <button
                      key={v.value}
                      onClick={() => setVerdict(v.value)}
                      className={`rounded-md px-3 py-1.5 text-xs font-bold border transition-all ${
                        verdict === v.value ? v.color + " scale-105 shadow-sm" : "border-border text-muted-foreground hover:border-foreground"
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.factcheck.format")}</label>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                  >
                    {FORMAT_VERIFICATION_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.factcheck.rubrique")}</label>
                  <select
                    value={categorieId}
                    onChange={(e) => setCategorieId(Number(e.target.value))}
                    className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-foreground"
                  >
                    <option value={0}>{t("admin.factcheck.none")}</option>
                    {categories?.map((c: any) => (
                      <option key={c.id} value={c.id}>{locale === "fr" ? c.libelleFr : c.libelleEn}</option>
                    ))}
                  </select>
                  {categorieSelectionnee && (
                    <p className="text-[10px] text-muted-foreground font-mono">slug : {categorieSelectionnee.slug}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Étape 2 : traductions ── */}
          {step === "traduction" && (
            <div className="space-y-4">
              {/* Récapitulatif métadonnées */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-surface rounded-md px-3 py-2">
                <Globe className="h-3.5 w-3.5 shrink-0" />
                {t("admin.factcheck.verdict.label")} <span className="font-semibold text-foreground">{VERDICTS.find(v => v.value === verdict)?.label}</span>
                <span className="mx-1">·</span>
                {t("admin.factcheck.format.label")} <span className="font-semibold text-foreground">{FORMAT_VERIFICATION_OPTIONS.find(f => f.value === format)?.label}</span>
                {categorieSelectionnee && (
                  <>
                    <span className="mx-1">·</span>
                    {t("admin.factcheck.rubrique.label")} <span className="font-semibold text-foreground">{locale === "fr" ? categorieSelectionnee.libelleFr : categorieSelectionnee.libelleEn}</span>
                  </>
                )}
              </div>

              {/* Sélecteur de langue */}
              {/* <div className="flex gap-2 flex-wrap">
                {LANGUES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => activeLang(l.code)}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors ${
                      langue === l.code
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                    }`}
                  >
                    <Globe className="h-3 w-3" />
                    {l.label}
                    {traductions[l.code]?.affirmation && (
                      <Check className="h-3 w-3 text-verified" />
                    )}
                  </button>
                ))}
              </div> */}

              {/* Champs de traduction */}
              <div className="space-y-3 rounded-md border border-border bg-surface/40 p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  {LANGUES.find((l) => l.code === langue)?.label}
                  {langue === "fr" && <span className="text-alert ml-1">*</span>}
                </h3>
                {TRAD_FIELDS.map(({ key, label, rows }) => (
                  <div key={key} className="space-y-1.5">
                    <label className="text-xs text-muted-foreground">{label}</label>
                    <textarea
                      value={traductions[langue]?.[key] || ""}
                      onChange={(e) => setTradField(langue, key, e.target.value)}
                      placeholder={label.replace(" *", "")}
                      rows={rows}
                      className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground resize-none"
                    />
                  </div>
                ))}
                {/* Aperçu du slug généré depuis l'affirmation FR */}
                {langue === "fr" && slugPreview && (
                  <p className="text-[10px] text-muted-foreground font-mono truncate">slug → {slugPreview}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer — même logique Retour/Suivant que ArticleModal */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          {step === "meta" ? (
            <>
              <button
                onClick={onClose}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
              >
                {t("admin.common.cancel")}
              </button>
              <button
                onClick={goToStep2}
                className="flex items-center gap-2 rounded-md bg-foreground px-5 py-2 text-sm font-semibold text-background hover:bg-foreground/90 transition-colors"
              >
                {t("admin.common.next")} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setStep("meta"); setError(""); }}
                className="flex items-center gap-1.5 rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> {t("admin.common.back")}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 rounded-md bg-foreground px-5 py-2 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                {t("admin.factcheck.new")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
function Factchecks() {
  useBloquerPartenaire();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [verdictFiltre, setVerdictFiltre] = useState<string | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<VerificationDto | null>(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 15;

  // Un compte qui n'a que le rôle REDACTEUR ne doit voir que ses propres
  // vérifications, pas celles de toute la rédaction (mirroring Content.tsx).
  const estRedacteurSeul = hasAnyRole(["REDACTEUR"]) && !hasAnyRole(["ADMIN", "EDITEUR"]);

  const { data: verificationsToutes, isLoading: loadingToutes, refetch: refetchToutes } = useVerifications(undefined, !estRedacteurSeul);
  const { data: mesVerifications, isLoading: loadingMiennes, refetch: refetchMiennes } = useMesVerifications(estRedacteurSeul);
  const { data: categories } = useCategories();
  const deleteVerif = useDeleteVerification();
  const validerVerif = useValiderVerification();
  const refuserVerif = useRefuserVerification();

  const isLoading = estRedacteurSeul ? loadingMiennes : loadingToutes;
  const refetch = estRedacteurSeul ? refetchMiennes : refetchToutes;

  const all: VerificationDto[] = (estRedacteurSeul ? mesVerifications : verificationsToutes) || [];

  const filtered = all.filter((v) => {
    const matchVerdict = !verdictFiltre || v.verdict === verdictFiltre;
    const matchSearch =
      !search ||
      v.traductions?.[0]?.affirmation?.toLowerCase().includes(search.toLowerCase()) ||
      v.slug?.toLowerCase().includes(search.toLowerCase());
    return matchVerdict && matchSearch;
  });

  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteVerif.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        refetch();
      },
      onError: () => toast.error(t("admin.common.deleteError")),
    });
  };

  return (
    <>
      <AdminTopbar title={t("admin.nav.factchecks")} subtitle={t("admin.factchecks.subtitle")} />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Barre d'outils */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setVerdictFiltre(undefined)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                !verdictFiltre ? "bg-foreground text-background" : "border border-border text-muted-foreground hover:text-foreground hover:bg-surface"
              }`}
            >
              {t("admin.factchecks.all")}
            </button>
            {VERDICTS.map((v) => (
              <button
                key={v.value}
                onClick={() => setVerdictFiltre(v.value)}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors ${
                  verdictFiltre === v.value ? v.color : "border-border text-muted-foreground hover:text-foreground hover:bg-surface"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder={t("admin.common.search")}
                className="rounded-md border border-border bg-surface pl-8 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground w-48"
              />
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1.5 rounded-md bg-foreground px-3 py-2 text-xs font-semibold text-background hover:bg-foreground/90 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> {t("admin.factchecks.new")}
            </button>
          </div>
        </div>

        {/* Table */}
        <ModulePanel title={`${t("search.verifications")} (${filtered.length})`}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">{t("admin.common.loading")}</span>
            </div>
          ) : paginated.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
              <ShieldCheck className="h-8 w-8 opacity-30" />
              <p className="text-sm">{t("admin.factchecks.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">{t("admin.common.col.claim")}</th>
                    <th className="px-3 py-2">Auteur</th>
                    <th className="px-3 py-2">{t("admin.common.col.verdict")}</th>
                    <th className="px-3 py-2">{t("admin.common.col.format")}</th>
                    <th className="px-3 py-2">{t("admin.common.col.status")}</th>
                    <th className="px-3 py-2">{t("admin.common.col.languages")}</th>
                    <th className="px-3 py-2">{t("admin.common.col.date")}</th>
                    <th className="px-3 py-2 text-right">{t("admin.common.col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((v) => (
                    <tr
                      key={v.id}
                      onClick={() => navigate(`/redaction/verifications/${v.id}`)}
                      className="border-b border-rule last:border-0 hover:bg-surface/60 group cursor-pointer"
                    >
                      <td className="px-3 py-2.5 max-w-sm">
                        <div className="font-medium text-foreground line-clamp-1">
                          « {v.traductions?.[0]?.affirmation || v.slug} »
                        </div>
                        {v.traductions?.[0]?.explicationCourte && (
                          <div className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">
                            {v.traductions[0].explicationCourte}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground">
                        {v.auteurNom || "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <VerdictBadge verdict={v.verdict} />
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="font-mono text-xs text-muted-foreground">{v.format}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <EditorialStatusBadge status={STATUT_MAP[v.statut || "PUBLIE"] || v.statut || "PUBLIE"} />
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="font-mono text-xs text-muted-foreground">
                          {v.traductions?.map((t) => t.langue.toUpperCase()).join(", ") || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(v.date).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-3 py-2.5">
                        <div
                          className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => navigate(`/redaction/verifications/${v.id}`)}
                            title="Gérer les traductions"
                            className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </button>
                          {!estRedacteurSeul && v.statut === "BROUILLON" && (
                            <>
                              <button
                                onClick={() =>
                                  validerVerif.mutate(v.id, {
                                    onSuccess: () => { toast.success("Vérification validée et publiée"); refetch(); },
                                    onError: () => toast.error(t("admin.common.saveError")),
                                  })
                                }
                                title="Valider (publier)"
                                className="rounded p-1 text-muted-foreground hover:text-verified hover:bg-verified/10 transition-colors"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() =>
                                  refuserVerif.mutate(v.id, {
                                    onSuccess: () => { toast.success("Vérification refusée"); refetch(); },
                                    onError: () => toast.error(t("admin.common.saveError")),
                                  })
                                }
                                title="Refuser"
                                className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                              >
                                <XIcon className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                          {!estRedacteurSeul && (
                            <button
                              onClick={() => setDeleteTarget(v)}
                              title="Supprimer"
                              className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border pt-4 mt-2">
              <span className="text-xs text-muted-foreground">{t("admin.common.page", { page: page + 1, total: totalPages })}</span>
              <div className="flex gap-2">
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-md border border-border p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </ModulePanel>
      </div>

      {showModal && (
        <VerificationModal
          onClose={() => setShowModal(false)}
          categories={categories || []}
          onSaved={() => refetch()}
        />
      )}

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("admin.factcheck.delete.title")}
        description={
          deleteTarget
            ? `« ${deleteTarget.traductions?.[0]?.affirmation || deleteTarget.slug} » ${t("admin.factcheck.delete.desc")}`
            : undefined
        }
        onConfirm={handleDelete}
        loading={deleteVerif.isPending}
      />
    </>
  );
}

export default Factchecks;
